/* @flow */
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import {connect} from 'react-redux';
import {checkThor, setThor, updateProductsPostThorCheck} from '../../actions/AppsActions';
import './CCInstallation.scss';
import AppsAnalytics from '../../analytics/AppsAnalytics';
import {Toast} from '@react/react-spectrum/Toast';
import Button from '@react/react-spectrum/Button';
import DownloadCoachMark from '../../components/DownloadCoachMark/DownloadCoachMark';
import CCInstallationDialog from './CCInstallationDialog/CCInstallationDialog';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import AppsUtil from '../../utils/AppsUtil';
import CSSUtil from '../../utils/CSSUtil';

type Props = {
	environment: string,
	locale: string,
	hasThor: boolean,
	checkThor: Function,
	intl: Object,
	imsData: Object,
	history: Object,
	setThor: Function,
	updateProductsPostThorCheck: Function,
	profile: Object
};

type
State = {
	showCCInstallationToast: boolean
};

class CCInstallation extends Component<Props, State> {
	setInitialToastState: Function;
	dialogKey: ?number;

	constructor(props: Props) {
		super(props);
		if (typeof AppsUtil.profile === 'undefined') {
			AppsUtil.profile = props.profile;
		}
		this.setInitialToastState = this.setInitialToastState.bind(this);
		AppsUtil.hideCCInstallationToast = this.hideToast.bind(this);
		AppsUtil.setImsData(this.props.imsData);
		this.state = {
			showCCInstallationToast: false
		};
		setTimeout(this.setInitialToastState,20000);
	}

	setInitialToastState() {
		let showCCInstallationToast = false;
		try {
			showCCInstallationToast = !(AppsUtil.sessionStorageGet('showCCInstallationToast') === 'false');
		}
		catch (err) {
			showCCInstallationToast = true;
			console.log(`${err} session storage not supported`);
		}
		if (this.props.hasThor === false && showCCInstallationToast === true && !AppsUtil.isPostPurchaseUser() && !AppsUtil.isMobileOrTab()) {
			this.sendAnalytic('render');
		}
		this.setState({showCCInstallationToast});

		// The Thor prompt should auto-dismiss 30 seconds after being shown
		setTimeout(AppsUtil.hideCCInstallationToast, 30000);
	}

	componentDidMount() {
		if (!this.isAppsURL()) {
			this.props.checkThor();
		}
		if (window.location.hash === '#cc-installation' && !document.getElementById('appsInstallationWrapper')) {
			this.props.history.replace(location.pathname + location.search);
		}
	}

	isAppsURL() {
		const path = window.location.pathname.split('/');
		if (path[1] === 'apps') {
			return true;
		}
		return false;
	}

	isDataAvailable() {
		return (typeof this.props.hasThor !== 'undefined' && this.props.hasThor === false && !AppsUtil.isPostPurchaseUser() && this.props.locale === 'en' && this.state.showCCInstallationToast && !AppsUtil.isMobileOrTab());
	}

	renderInstallationDialog() {
		return (
			<CCInstallationDialog {...this.props} eventSource='toast' closeCCInstallationDialog={this.closeCCInstallationDialog.bind(this)} />
		);
	}

	hideToast() {
		try {
			AppsUtil.sessionStorageSet('showCCInstallationToast', 'false');
		}
		catch (err) {
			console.log(`${err} session storage not supported`);
		}
		this.state.showCCInstallationToast && this.setState({showCCInstallationToast: false});
	}

	openDialog() {
		if ((document.querySelector('#popUpInstaller') === null)) {
			ReactDOM.render(<DownloadCoachMark isNewDesign={true} intl={this.props.intl}/>, CSSUtil.getCoachContainerElement());
		}
		this.dialogKey = ModalContainer.show(this.renderInstallationDialog(),this);
		window.location.hash = 'cc-installation';

		// Since user has taken acion, do not show CC Installation banner/toast now onwards
		try {
			AppsUtil.sessionStorageSet('showCCInstallationBanner', 'false');
		}
		catch (err) {
			console.log(`${err} session storage not supported`);
		}
		this.hideToast();
	}

	componentWillUnmount() {
		CSSUtil.hideInstallerPopUp(10000);
	}

	componentDidUpdate() {
		if (typeof AppsUtil.profile === 'undefined') {
			AppsUtil.profile = this.props.profile;
		}
		if (window.location.hash !== '#cc-installation' && document.getElementById('appsInstallationWrapper')) {
			this.closeCCInstallationDialog(true);
		}
	}

	closeCCInstallationDialog(closePopUpImmediately: ?boolean) {
		if (closePopUpImmediately) {
			// Hide immediately
			CSSUtil.hideInstallerPopUp(10);
		}
		else {
			// Hide after 10 seconds
			CSSUtil.hideInstallerPopUp(10000);
		}
		if (this.dialogKey) {
			ModalContainer.hide(this.dialogKey);
			this.dialogKey = null;
		}
	}

	sendAnalytic(type: string) {
		const appsAnalytics = new AppsAnalytics(this.props.environment);
		appsAnalytics.initAnalytics();
		const appConfig = {sapCode:'KCCC', displayName:'Creative Cloud', entitled: !AppsUtil.isUserFree()};

		switch (type) {
			case 'render':
				appsAnalytics.genericAnalyticEvent(appConfig, 'render','cc-installation-toast','toast');
				break;
			case 'install':
				appsAnalytics.genericAnalyticEvent(appConfig, 'click','cc-installation-install','toast');
				break;
			case 'close':
				appsAnalytics.genericAnalyticEvent(appConfig, 'click','cc-installation-close','toast');
				AppsUtil.setCCInstallationToastXButtonState('clicked');
				break;
		}
	}

	openPDP() {
		this.props.history.push(AppsUtil.getCCWebURL('KCCC','desktop',false,false),{source:'toastClick'});
		this.hideToast();
	}

	render() {
		return (
			this.isDataAvailable() && <Toast onClick={() => {this.openPDP();}} className='ccinstall-toast ccweb-design' onClose={(e) => { e.stopPropagation(); this.hideToast(); this.sendAnalytic('close');}} closable variant='info'>
				<div className='ccinstall-toast-body'>
					<span className='ccinstall-toast-body-desc'>{this.props.intl.formatMessage({id: 'apps.CCInstallation.desc'})}</span>
					<Button
						label={this.props.intl.formatMessage({id: 'apps.tile.download'})}
						variant='cta'
						className='spectrum-ClearButton--overBackground'
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							this.sendAnalytic('install');
							this.openDialog();
						}}
					/>
				</div>
			</Toast>
		);
	}
}

function mapStateToProps(state) {
	return {
		hasThor: state.apps.hasThor
	};
}


export default

connect(mapStateToProps, {checkThor, setThor, updateProductsPostThorCheck})(CCInstallation);
